import React, { useContext } from 'react';
import { Button, Tooltip } from 'antd';
import { EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { ThemeContext } from '../../contexts/ThemeContext';

export interface ITableActionButtonProps {
    action?: 'view' | 'edit' | 'delete' | 'custom';
    tooltip?: string;
    icon?: React.ReactNode;
    color?: string;
    bgColor?: string;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
    style?: React.CSSProperties;
    disabled?: boolean;
    loading?: boolean;
}

export default function TableActionButton({
    action = 'view',
    tooltip,
    icon,
    color,
    bgColor,
    onClick,
    style,
    disabled = false,
    loading = false,
}: ITableActionButtonProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    let defaultIcon = icon;
    let defaultTooltip = tooltip;
    let defaultBg = bgColor;

    if (action === 'view') {
        defaultIcon = defaultIcon || <EyeOutlined style={{ fontSize: 15, color: color || '#1677ff' }} />;
        defaultTooltip = defaultTooltip || 'Visualizar detalhes';
        defaultBg = defaultBg || (isDark ? 'rgba(22, 119, 255, 0.12)' : '#e6f4ff');
    } else if (action === 'edit') {
        defaultIcon = defaultIcon || <EditOutlined style={{ fontSize: 15, color: color || '#faad14' }} />;
        defaultTooltip = defaultTooltip || 'Editar registro';
        defaultBg = defaultBg || (isDark ? 'rgba(250, 173, 20, 0.12)' : '#fffbe6');
    } else if (action === 'delete') {
        defaultIcon = defaultIcon || <DeleteOutlined style={{ fontSize: 15, color: color || '#ff4d4f' }} />;
        defaultTooltip = defaultTooltip || 'Excluir registro';
        defaultBg = defaultBg || (isDark ? 'rgba(255, 77, 79, 0.12)' : '#fff1f0');
    }

    let renderedIcon = defaultIcon;
    if (disabled && React.isValidElement(renderedIcon)) {
        renderedIcon = React.cloneElement(renderedIcon as React.ReactElement<any>, {
            style: {
                ...((renderedIcon as any).props?.style || {}),
                color: isDark ? 'rgba(255, 255, 255, 0.25)' : '#94a3b8',
            },
        });
    }

    const button = (
        <Button
            type="text"
            shape="circle"
            className="table-action-btn"
            icon={renderedIcon}
            disabled={disabled}
            loading={loading}
            style={{
                width: 32,
                height: 32,
                minWidth: 32,
                backgroundColor: disabled
                    ? (isDark ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc')
                    : defaultBg,
                border: `1px solid ${
                    disabled
                        ? (isDark ? 'rgba(255, 255, 255, 0.08)' : '#e2e8f0')
                        : (isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.06)')
                }`,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                transition: 'all 0.2s ease',
                ...style,
            }}
            onClick={(e) => {
                if (disabled) return;
                e.stopPropagation();
                onClick?.(e);
            }}
        />
    );

    if (defaultTooltip) {
        if (disabled) {
            return (
                <Tooltip title={defaultTooltip}>
                    <span style={{ display: 'inline-flex', cursor: 'not-allowed' }}>
                        {button}
                    </span>
                </Tooltip>
            );
        }
        return <Tooltip title={defaultTooltip}>{button}</Tooltip>;
    }

    return button;
}

